"use client";

import { useEffect, useRef } from "react";

const BEFORE_DOCS = [
  { name: "VX_Q3_2025_Earnings.pdf", size: "2.4 MB", type: "Earnings", color: "#6366F1" },
  { name: "10-Q_Oct2025_SEC.pdf", size: "8.1 MB", type: "SEC Filing", color: "#F5A623" },
  { name: "CASGEVY_Phase3_Data.pdf", size: "12.7 MB", type: "Clinical", color: "#F43F5E" },
  { name: "ESG_Report_2025.pdf", size: "3.2 MB", type: "ESG", color: "#0FF4C6" },
  { name: "Pipeline_Update_H2.pdf", size: "1.8 MB", type: "Pipeline", color: "#A78BFA" },
];

export default function AhaMomentSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal, .reveal-left, .reveal-right").forEach((el, i) => {
              setTimeout(() => el.classList.add("visible"), i * 100);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-pad relative overflow-hidden"
      id="platform"
      style={{ background: "#0B0F1A" }}
    >
      {/* Subtle grid */}
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16 reveal">
          <span className="section-label justify-center">How it works</span>
          <h2
            className="font-display text-4xl lg:text-5xl font-medium text-white leading-tight"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            From raw documents to{" "}
            <span className="gradient-text-teal italic">verified intelligence</span>
          </h2>
          <p className="mt-4 text-[#8892A4] text-lg max-w-2xl mx-auto leading-relaxed">
            Stop manually searching through 400-page filings. Myndlayer retrieves the exact passages that answer your question and shows you exactly where they came from.
          </p>
        </div>

        {/* Before / After comparison */}
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">

          {/* BEFORE */}
          <div
            className="rounded-2xl p-6 lg:p-8 reveal-left"
            style={{ background: "#111827", border: "1px solid rgba(255,255,255,0.06)" }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div
                className="px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider"
                style={{ background: "rgba(244,63,94,0.1)", color: "#F43F5E", border: "1px solid rgba(244,63,94,0.2)" }}
              >
                Before
              </div>
              <span className="text-[#4B5568] text-sm">Manual research</span>
            </div>

            {/* Document pile visual */}
            <div className="space-y-3 mb-6">
              {BEFORE_DOCS?.map((doc, i) => (
                <div
                  key={doc?.name}
                  className="flex items-center gap-3 p-3 rounded-xl transition-all"
                  style={{
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.05)",
                    transform: `rotate(${(i % 2 === 0 ? 1 : -1) * 0.4}deg)`,
                  }}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold"
                    style={{ background: `${doc?.color}15`, color: doc?.color, border: `1px solid ${doc?.color}25` }}
                  >
                    PDF
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[#8892A4] truncate">{doc?.name}</p>
                    <p className="text-[10px] text-[#4B5568]">{doc?.size} · {doc?.type}</p>
                  </div>
                  <div className="text-[10px] text-[#4B5568] font-mono">?</div>
                </div>
              ))}
            </div>

            {/* Pain indicators */}
            <div className="space-y-2">
              {[
                "⏱ 3–6 hours per research question",
                "😰 Miss critical data buried in appendices",
                "❌ No audit trail for sourcing claims",
              ]?.map((pain) => (
                <div key={pain} className="flex items-center gap-2 text-xs text-[#4B5568]">
                  <span>{pain}</span>
                </div>
              ))}
            </div>
          </div>

          {/* AFTER */}
          <div
            className="rounded-2xl p-6 lg:p-8 reveal-right relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #0F1D2A, #111827)",
              border: "1px solid rgba(15,244,198,0.15)",
              boxShadow: "0 0 60px rgba(15,244,198,0.05)",
            }}
          >
            {/* Glow top */}
            <div
              className="absolute top-0 right-0 w-64 h-64 pointer-events-none"
              style={{ background: "radial-gradient(ellipse at top right, rgba(15,244,198,0.08), transparent)" }}
            />

            <div className="flex items-center gap-3 mb-6">
              <div className="agent-badge-live">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0FF4C6] animate-teal-pulse inline-block" />
                After · Myndlayer
              </div>
            </div>

            {/* Answer card */}
            <div
              className="rounded-xl p-4 mb-4"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
            >
              <div className="flex items-center gap-2 mb-3">
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-[#0B0F1A]"
                  style={{ background: "linear-gradient(135deg, #0FF4C6, #38BDF8)" }}
                >
                  M
                </div>
                <span className="text-[10px] text-[#4B5568]">IR Agent · 1.2s response</span>
              </div>
              <p className="text-xs text-[#C8D0DC] leading-[1.8]">
                Full-year guidance revised to{" "}
                <span className="gradient-text-teal font-semibold">$10.8–11.0B</span>
                , exceeding Bloomberg consensus by{" "}
                <span className="gradient-text-amber font-semibold">340bps</span>
                . Beat driven by CASGEVY milestones and CF franchise outperformance.
              </p>

              {/* Cited passages */}
              <div className="mt-3 space-y-2">
                <div className="citation-highlight">
                  <span className="text-[#4B5568] font-mono text-[10px]">VX Q3 2025 Earnings · p.4 · </span>
                  <span className="text-[#8892A4] text-[10px]">"…we are raising our full year net product revenue guidance to $10.8 to $11.0 billion…"</span>
                </div>
                <div className="citation-highlight" style={{ borderLeftColor: "rgba(245,166,35,0.5)", background: "linear-gradient(135deg, rgba(245,166,35,0.06), rgba(245,166,35,0.03))" }}>
                  <span className="text-[#4B5568] font-mono text-[10px]">10-Q Oct 2025 · §MD&A · </span>
                  <span className="text-[#8892A4] text-[10px]">"…CASGEVY royalty and milestone recognition of $847M…"</span>
                </div>
              </div>
            </div>

            {/* Success indicators */}
            <div className="space-y-2">
              {[
                { icon: "⚡", text: "Answer in 1.2 seconds", color: "#0FF4C6" },
                { icon: "🔗", text: "Every claim linked to source passage", color: "#0FF4C6" },
                { icon: "✅", text: "Full audit trail for compliance", color: "#0FF4C6" },
              ]?.map((item) => (
                <div key={item?.text} className="flex items-center gap-2 text-xs" style={{ color: item?.color }}>
                  <span>{item?.icon}</span>
                  <span className="text-[#8892A4]">{item?.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stat row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-12 reveal">
          {[
            { value: "1.2s", label: "Avg. answer latency", suffix: "" },
            { value: "97.4%", label: "Citation accuracy rate", suffix: "" },
            { value: "2.4M", label: "Passages indexed", suffix: "" },
            { value: "6h → 90s", label: "Research time saved", suffix: "" },
          ]?.map((stat) => (
            <div key={stat?.label} className="stat-card text-center">
              <div
                className="text-3xl font-display font-semibold mb-1"
                style={{ fontFamily: "Fraunces, serif", color: "#0FF4C6" }}
              >
                {stat?.value}
              </div>
              <div className="text-xs text-[#4B5568] uppercase tracking-wider">{stat?.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}